// Orchard app-compat shell: DOM wiring, fetch, and rendering around the pure
// logic in @orchard/app-compat-core (resolved via the theme importmap in
// snippets/scripts.liquid). Structured as independently-guarded feature
// initializers (search, suggestions, mobile modal, scroll buttons) so a
// missing element or a failure in one feature disables that feature instead
// of killing the whole tool.
import {
  getRecommendedCard,
  recommendedModelSort,
  getRequiredOs,
  generateProductCardHTML,
  filterCollections,
  cleanVersionData,
  reformatCollectionsData,
} from '@orchard/app-compat-core';

let selectedValue = '';
let mobileSelectedValue = '';
let currentFocus = -1; // keyboard navigation for suggestions

// =========================================================================
// Search pipeline
// =========================================================================

function updatePlaceholderWithMediaQuery() {
  const inputElement = document.getElementById('search-input');
  if (!inputElement) return;
  const mediaQuery = window.matchMedia('(max-width: 768px)');

  if (mediaQuery.matches) {
    inputElement.setAttribute('placeholder', 'New Search');
    inputElement.readOnly = true;
  } else {
    inputElement.setAttribute('placeholder', 'Enter App name');
    inputElement.readOnly = false;
  }
}

/**
 * Formats API data to display in the DOM.
 * @param {Array} cleanedData - List of collections.
 * @param {Array} data - Data returned from the API.
 * @param {String} device - Device type.
 * @returns {String[]} - HTML strings to inject into the DOM, one per collection.
 */
function formatAppCompatResults(cleanedData, data, device) {
  const firstResult = data ? data[0] : null;
  if (!firstResult) {
    return [];
  }

  const currentYear = (new Date()).getFullYear();
  // The shell is the one place theme settings are read; the core pipeline
  // takes the current iOS major as an explicit parameter.
  const currentMajorIosVersion = window.appCompatSettings?.currentMajorIosVersion;
  const filteredCollections = filterCollections(cleanedData, firstResult, device, currentYear, currentMajorIosVersion);
  const isMobile = window.matchMedia('(max-width: 768px)').matches;

  const withRecommendedCards = filteredCollections.map((collection) => {
    const recommendedCard = getRecommendedCard(collection.products, firstResult, device, currentYear, currentMajorIosVersion);
    return {
      ...collection,
      recommendedCard: recommendedCard,
      products: removeRecommendedProduct(recommendedCard, collection.products),
    };
  });

  // Filter out collections that have no compatible devices (no recommended card AND no products)
  const eligibleCollections = withRecommendedCards.filter(collection =>
    collection.products.length > 0 || collection.recommendedCard !== null
  );

  return eligibleCollections.map((collection) => generateProductCardHTML(collection, firstResult, isMobile, device));
}

function removeRecommendedProduct(recommendedProduct, productsData) {
  if (!recommendedProduct || !recommendedProduct.id) {
    return productsData;
  }
  return productsData.filter((product) => product.id !== recommendedProduct.id);
}

/**
 * Validates the search inputs and updates the UI accordingly.
 * @param {string} appName - The name of the app to search for.
 * @param {string} selectedDevice - The selected device type.
 * @returns {boolean} - Returns true if inputs are valid, false otherwise.
 */
function validateSearchInputs(appName, selectedDevice) {
  let isValid = true;
  const searchInput = document.getElementById('search-input');

  if (!appName || appName.trim() === '') {
    if (searchInput) {
      searchInput.setCustomValidity('Please enter an app name');
      searchInput.reportValidity();
    }
    isValid = false;
  } else if (searchInput) {
    searchInput.setCustomValidity('');
  }

  const customSelect = document.getElementById('customSelect');
  if (selectedDevice === '') {
    if (customSelect) customSelect.classList.add('error');
    isValid = false;
  } else if (customSelect) {
    customSelect.classList.remove('error');
  }

  return isValid;
}

/**
 * Maps search terms to their correct database equivalents.
 * @param {string} appName - The search term entered by the user.
 * @returns {string} - The mapped app name or original name if no mapping exists.
 */
function mapSearchTerm(appName) {
  const lowerCaseName = appName.toLowerCase();
  const mappings = window.appCompatSettings?.searchTermMappings || {};
  return mappings[lowerCaseName] || appName;
}

/**
 * Checks if a search term has a mapping (to skip Slack alerts).
 * @param {string} appName - The search term entered by the user.
 * @returns {boolean} - True if the term is mapped, false otherwise.
 */
function isSearchTermMapped(appName) {
  const lowerCaseName = appName.toLowerCase();
  const mappings = window.appCompatSettings?.searchTermMappings || {};
  return mappings.hasOwnProperty(lowerCaseName);
}

/**
 * Fetches app compatibility data from the API. Throws on HTTP errors so
 * the processSearch boundary can show the error state — an API failure
 * must not masquerade as "app not in our database yet".
 * @param {string} appName - The name of the app to search for.
 * @returns {Promise<Array>} - Resolves to the API response data.
 */
async function fetchAppCompatData(appName) {
  const mappedAppName = mapSearchTerm(appName);
  const response = await fetch('https://api.getorchard.com/v1/app-compat/search?app=' + encodeURIComponent(mappedAppName));
  if (!response.ok) {
    throw new Error(`app-compat search returned HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Datadog RUM breadcrumbs for alerting on search outcomes. DD_RUM only
 * exists on the production host (snippets/orchard-datadog-rum.liquid) and
 * loads async, so calls queue through onReady; anywhere else these no-op.
 * Telemetry must never break the search flow.
 */
function rumAction(name, context) {
  try {
    window.DD_RUM?.onReady(() => window.DD_RUM.addAction(name, context));
  } catch { /* no-op */ }
}

function rumError(error, context) {
  try {
    window.DD_RUM?.onReady(() => window.DD_RUM.addError(error, context));
  } catch { /* no-op */ }
}

/**
 * Error state for the whole search flow: hides results and reuses the
 * no-results area with a "try again" message (replaces the old alert()).
 */
function showSearchError() {
  const resultsSection = document.getElementById('results-section');
  if (resultsSection) resultsSection.style.display = 'none';

  const noResultsSection = document.getElementById('no-results-section');
  if (noResultsSection) {
    noResultsSection.style.display = 'flex';
    noResultsSection.innerHTML = `
      <div id="no-results-message">
        Something went wrong while checking compatibility. Please try again in a moment.
      </div>
    `;
  }
}

/**
 * Displays the app compatibility search results in the DOM.
 */
async function displaySearchResults(cleanedData, appName, device) {
  const data = await fetchAppCompatData(appName);

  const resultsSection = document.getElementById('results-section');
  const resultsDiv = document.getElementById('search-results');
  const noResultsSection = document.getElementById('no-results-section');
  if (!resultsSection || !resultsDiv || !noResultsSection) {
    throw new Error('app-compat results markup missing');
  }
  resultsDiv.innerHTML = '';

  const emailInputContainer = document.querySelector('.email-input-container');
  if (emailInputContainer) {
    emailInputContainer.remove();
  }

  if (!data || data.length === 0) {
    rumAction('app_compat_no_results', { app: appName, device, reason: 'app_not_found' });
    resultsSection.style.display = 'none';
    noResultsSection.style.display = 'flex';
    noResultsSection.innerHTML = `
    <div id="no-results-message">
      Unfortunately, our database doesn’t include your app (yet!).

      Please check back in one business day and it will be added 😊
    </div>
  `;
  } else {
    const firstResult = data[0];
    const foundApp = firstResult.name;
    const requiredOs = getRequiredOs(firstResult, device);
    const osType = getOsType(device);
    resultsSection.style.display = 'block';
    noResultsSection.style.display = 'none';

    const messageDiv = document.getElementById('search-message');
    if (messageDiv) {
      messageDiv.innerText = `${foundApp} requires ${osType} ${requiredOs}. The following ${device}s are compatible:`;
    }
    const sections = formatAppCompatResults(cleanedData, data, device);
    resultsDiv.innerHTML = sections.join('');
    // App known but zero compatible devices rendered — the signature of a
    // data problem (e.g. a bad last_os_supported metafield blanking every
    // search, 2026-08-14), distinct from an honest "app not found".
    if (sections.length === 0) {
      rumAction('app_compat_no_results', { app: appName, device, reason: 'no_compatible_devices' });
    } else {
      rumAction('app_compat_result', { app: appName, device, sections: sections.length });
    }

    attachModalEventListeners();
  }
}

function getOsType(device) {
  if (device.toLowerCase() === "iphone") {
    return "iOS";
  } else if (device.toLowerCase() === "ipad") {
    return "iPadOS";
  } else {
    throw new Error(`Unsupported device type: ${device}`);
  }
}

const debounceNotify = (() => {
  let lastAppName = '';
  let lastNotifyTime = 0;
  const DEBOUNCE_DELAY = 2000; // 2 seconds debounce

  return async (appName) => {
    const now = Date.now();
    if (appName !== lastAppName || (now - lastNotifyTime) > DEBOUNCE_DELAY) {
      lastAppName = appName;
      lastNotifyTime = now;
      try {
        await fetch(`https://api.getorchard.com/v1/app-compat/notify-on-search?app=${encodeURIComponent(appName)}`);
      } catch (error) {
        console.error('[app-compat] Error notifying search:', error);
      }
    }
  };
})();

function readCollectionsDataFromDom() {
  return Array.from(document.querySelectorAll('.collection-data')).map(collectionElement => {
    return {
      collection_title: collectionElement.dataset.collectionTitle,
      collection_handle: collectionElement.dataset.collectionHandle,
      id: collectionElement.dataset.id,
      available: collectionElement.dataset.available === 'true',
      title: collectionElement.dataset.title,
      model: collectionElement.dataset.model,
      processor: collectionElement.dataset.processor,
      works_until: collectionElement.dataset.worksUntil,
      url: collectionElement.dataset.url,
      featured_image: collectionElement.dataset.featuredImage,
      last_os_supported: collectionElement.dataset.lastOsSupported,
      price: collectionElement.dataset.price,
    };
  });
}

/**
 * The full search flow, behind one error boundary: any throw anywhere in
 * the pipeline (bad data attribute, missing DOM node, API failure) shows
 * the error state instead of dying as an unhandled rejection that leaves
 * the button silently doing nothing.
 */
async function processSearch(e, selectedDevice, appName) {
  e.preventDefault();
  try {
    if (!validateSearchInputs(appName, selectedDevice)) {
      return;
    }

    // gtag may be absent (consent tooling, adblockers) — analytics must
    // never break search.
    window.gtag?.('event', 'app_compat_search', {
      'appName': appName,
      'device': selectedDevice,
    });

    // Skip Slack alerts for mapped search terms since they're known good searches
    if (!isSearchTermMapped(appName)) {
      await debounceNotify(appName);
    }

    const collectionsData = readCollectionsDataFromDom();
    const reformattedData = reformatCollectionsData(collectionsData);
    const cleanedData = reformattedData.map((collection) => {
      return {
        ...collection,
        products: cleanVersionData(collection.products)
      };
    });
    const sortedData = cleanedData.map((collection) => {
      return {
        ...collection,
        products: collection.products.sort(recommendedModelSort)
      };
    });

    await displaySearchResults(sortedData, appName, selectedDevice);
  } catch (error) {
    console.error('[app-compat] Search failed', error);
    rumError(error, { feature: 'app-compat', app: appName, device: selectedDevice });
    showSearchError();
  }
}

// =========================================================================
// App caching and fuzzy search
// =========================================================================

const APP_CACHE_KEY = 'orchard_apps_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
let allApps = [];
let fuse = null;

function isCacheExpired(cachedData) {
  const now = Date.now();
  return (now - cachedData.timestamp) > CACHE_DURATION;
}

function readCachedApps() {
  try {
    const cachedData = localStorage.getItem(APP_CACHE_KEY);
    return cachedData ? JSON.parse(cachedData) : null;
  } catch (error) {
    // localStorage access or JSON.parse can throw (private mode, quota,
    // corrupted cache) — treat as a cache miss.
    return null;
  }
}

async function loadAllApps() {
  const cached = readCachedApps();
  if (cached && !isCacheExpired(cached)) {
    return cached.data;
  }

  try {
    const response = await fetch('https://api.getorchard.com/v1/app-compat/app-names');
    if (!response.ok) {
      throw new Error(`app-names returned HTTP ${response.status}`);
    }
    const apps = await response.json();

    try {
      localStorage.setItem(APP_CACHE_KEY, JSON.stringify({
        data: apps,
        timestamp: Date.now()
      }));
    } catch (error) {
      // Failing to cache is fine; suggestions still work this page load.
    }

    return apps;
  } catch (error) {
    console.error('[app-compat] Error loading apps:', error);
    // Fall back to cached data even if expired.
    return cached ? cached.data : [];
  }
}

async function initializeFuzzySearch() {
  if (!allApps.length) {
    allApps = await loadAllApps();
  }

  if (typeof Fuse !== 'undefined' && allApps.length > 0) {
    fuse = new Fuse(allApps, {
      keys: ['app_name'],
      threshold: 0.2, // Lower = more strict, higher = more fuzzy
      includeScore: true,
      ignoreLocation: true,
      shouldSort: true,
      minMatchCharLength: 2
    });
  }
}

function performFuzzySearch(query) {
  if (!fuse || !query.trim()) {
    return [];
  }

  const results = fuse.search(query);

  // convert Fuse results back to our expected format and limit to top 10
  return results.slice(0, 10).map(result => ({
    name: result.item.app_name,
    score: result.score,
    indices: result.matches ? result.matches[0].indices : []
  }));
}

function highlightMatches(text, query) {
  if (!query) return text;

  // Simple highlighting - wrap matching characters in <mark> tags
  const regex = new RegExp(`(${query.split('').map(char =>
    char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');

  return text.replace(regex, '<mark>$1</mark>');
}

const debounceSearch = (func, delay) => {
  let debounceTimer;
  return function(...args) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => func.apply(this, args), delay);
  };
}

async function handleSuggestions(inputElement, suggestionsElement) {
  const query = inputElement.value.trim();

  if (query.length === 0) {
    suggestionsElement.innerHTML = '';
    return;
  }

  try {
    // Ensure fuzzy search is initialized
    if (!fuse) {
      await initializeFuzzySearch();
    }

    const data = performFuzzySearch(query);

    suggestionsElement.innerHTML = '';
    currentFocus = -1; // reset current focus

    data.forEach(item => {
      const li = document.createElement('li');
      li.innerHTML = highlightMatches(item.name, query);
      li.classList.add('suggestion-item');

      li.addEventListener('click', () => {
        inputElement.value = item.name;
        suggestionsElement.innerHTML = '';
      });

      suggestionsElement.appendChild(li);
    });
  } catch (error) {
    console.error('[app-compat] Error in fuzzy search:', error);
    // Fallback to the API search if fuzzy search fails
    try {
      const response = await fetch(`https://api.getorchard.com/v1/app-compat/search?app=${encodeURIComponent(query)}`);
      if (!response.ok) {
        throw new Error(`search returned HTTP ${response.status}`);
      }
      const data = await response.json();

      suggestionsElement.innerHTML = '';
      currentFocus = -1;

      data.forEach(item => {
        const li = document.createElement('li');
        li.textContent = item.name;
        li.classList.add('suggestion-item');

        li.addEventListener('click', () => {
          inputElement.value = item.name;
          suggestionsElement.innerHTML = '';
        });

        suggestionsElement.appendChild(li);
      });
    } catch (fallbackError) {
      console.error('[app-compat] Fallback search also failed:', fallbackError);
    }
  }
}

// =========================================================================
// Modals
// =========================================================================

function openSearchModal() {
  const modal = document.getElementById('mobile-search-modal');
  if (!modal) {
    console.error('[app-compat] Modal element not found');
    return;
  }
  const searchInput = document.getElementById('search-input');
  if (searchInput) searchInput.value = '';
  modal.style.display = 'flex';
  document.body.style.overflowY = 'hidden';
  // The chat widget (tawk) overlaps the modal; clear it if present. It may
  // legitimately be absent — never let its absence break mobile search.
  const chatWidget = document.querySelector('.widget-visible');
  if (chatWidget) chatWidget.innerHTML = '';
}

function closeSearchModal() {
  const modal = document.getElementById('mobile-search-modal');
  if (!modal) return;
  modal.style.display = 'none';
  document.body.style.overflowY = 'visible';
}

function openInfoModal(modal) {
  modal.style.display = 'block';
  modal.querySelector('.info-modal-content')?.focus();
}

function closeInfoModal(modal) {
  modal.style.display = 'none';
}

function attachModalEventListeners() {
  const infoIcons = document.querySelectorAll('.info-icon');
  const closeButtons = document.querySelectorAll('.close-button');

  infoIcons.forEach(infoIcon => {
    const productId = infoIcon.id.split('-')[1];
    const modal = document.getElementById(`info-modal-${productId}`);
    if (!modal) return;

    infoIcon.addEventListener('click', function() {
      openInfoModal(modal);
    });
  });

  closeButtons.forEach(closeButton => {
    const productId = closeButton.id.split('-')[1];
    const modal = document.getElementById(`info-modal-${productId}`);
    if (!modal) return;

    closeButton.addEventListener('click', function() {
      closeInfoModal(modal);
    });
  });
}

// =========================================================================
// Feature initializers — each guards its own DOM and fails independently
// =========================================================================

function initDesktopSearch() {
  const customSelect = document.getElementById('customSelect');
  const searchButtonEl = document.getElementById('search-button');
  const searchInput = document.getElementById('search-input');
  if (!customSelect || !searchButtonEl || !searchInput) {
    console.warn('[app-compat] Desktop search elements missing — search disabled');
    return;
  }

  const selectSelected = customSelect.querySelector('.select-selected');
  const selectItems = customSelect.querySelector('.select-items');
  if (selectSelected && selectItems) {
    const selectOptions = selectItems.querySelectorAll('.select-option');

    selectSelected.addEventListener('click', function(e) {
      e.stopPropagation();
      selectItems.classList.toggle('select-hide');
    });

    selectOptions.forEach(option => {
      option.addEventListener('click', function() {
        selectSelected.textContent = this.textContent;
        selectItems.classList.add('select-hide');
        selectedValue = this.textContent;
        customSelect.classList.remove('error');
      });
    });

    document.addEventListener('click', function(e) {
      if (e.target !== selectSelected) {
        selectItems.classList.add('select-hide');
      }
    });
  }

  const clearButton = document.querySelector('.clear-button');
  if (clearButton) {
    searchInput.addEventListener('input', function() {
      clearButton.style.display = this.value.length > 0 ? 'block' : 'none';
    });

    clearButton.addEventListener('click', function() {
      searchInput.value = '';
      clearButton.style.display = 'none';
      searchInput.focus();
    });
  }

  searchButtonEl.addEventListener('click', async function(e) {
    const appName = searchInput.value.trim();
    await processSearch(e, selectedValue, appName);
  });

  // Mobile taps on the (read-only) input open the search modal instead.
  searchInput.addEventListener('click', function() {
    if (window.innerWidth <= 768) {
      openSearchModal();
    }
  });

  updatePlaceholderWithMediaQuery();
  window.matchMedia('(max-width: 768px)').addEventListener('change', updatePlaceholderWithMediaQuery);
}

function initSuggestions() {
  const searchInput = document.getElementById('search-input');
  const suggestionsList = document.getElementById('suggestions-list');
  if (!searchInput || !suggestionsList) {
    console.warn('[app-compat] Suggestions elements missing — suggestions disabled');
    return;
  }

  searchInput.addEventListener('input', debounceSearch(function() {
    handleSuggestions(searchInput, suggestionsList);
  }, 300));

  const searchContainer = document.querySelector('.app-compat-search');
  document.addEventListener('click', function(e) {
    if (searchContainer && !searchContainer.contains(e.target)) {
      suggestionsList.innerHTML = '';
    }
  });
}

function initMobileModal() {
  const modal = document.getElementById('mobile-search-modal');
  const modalSearchInput = document.getElementById('modal-search-input');
  const modalCustomSelect = document.getElementById('modalCustomSelect');
  if (!modal || !modalSearchInput || !modalCustomSelect) {
    console.warn('[app-compat] Mobile modal elements missing — mobile search disabled');
    return;
  }

  // Prevent modal from closing when clicking inside content
  modal.addEventListener('click', function(event) {
    event.stopPropagation();
  });

  const mobileSuggestionsList = document.createElement('ul');
  mobileSuggestionsList.id = 'mobile-suggestions-list';
  mobileSuggestionsList.classList.add('suggestions-list');
  modalSearchInput.insertAdjacentElement('afterend', mobileSuggestionsList);

  modalSearchInput.addEventListener('input', debounceSearch(function() {
    handleSuggestions(modalSearchInput, mobileSuggestionsList);
  }, 300));

  const modalContent = document.querySelector('.mobile-search-modal-content');
  document.addEventListener('click', function(e) {
    if (modalContent && !modalContent.contains(e.target)) {
      mobileSuggestionsList.innerHTML = '';
    }
  });

  const closeModalButton = document.querySelector('.mobile-close-modal');
  if (closeModalButton) {
    closeModalButton.addEventListener('click', closeSearchModal);
  }

  const newSearchButton = document.getElementById('new-search-button');
  if (newSearchButton) {
    newSearchButton.addEventListener('click', function() {
      const resultsDiv = document.getElementById('search-results');
      if (resultsDiv) resultsDiv.innerHTML = '';
      const noResultsMessageDiv = document.getElementById('no-results-message');
      if (noResultsMessageDiv) noResultsMessageDiv.innerHTML = '';
      modalSearchInput.value = '';
    });
  }

  const modalSelectSelected = modalCustomSelect.querySelector('.modal-select-selected');
  const modalSelectItems = modalCustomSelect.querySelector('.modal-select-items');
  if (modalSelectSelected && modalSelectItems) {
    const modalSelectOptions = modalSelectItems.querySelectorAll('.modal-select-option');

    modalSelectSelected.addEventListener('click', function(e) {
      e.stopPropagation();
      modalSelectItems.classList.toggle('modal-select-hide');
    });

    modalSelectOptions.forEach(option => {
      option.addEventListener('click', function() {
        modalSelectSelected.textContent = this.textContent;
        modalSelectItems.classList.add('modal-select-hide');
        mobileSelectedValue = this.textContent;
      });
    });

    document.addEventListener('click', function(e) {
      if (e.target !== modalSelectSelected) {
        modalSelectItems.classList.add('modal-select-hide');
      }
    });
  }

  const modalSearchButton = document.getElementById('modal-search-button');
  if (modalSearchButton) {
    modalSearchButton.addEventListener('click', async function(e) {
      e.preventDefault();
      closeSearchModal();
      const appName = modalSearchInput.value;

      const searchInput = document.getElementById('search-input');
      if (searchInput) searchInput.value = '';

      const resultsDiv = document.getElementById('search-results');
      if (resultsDiv) resultsDiv.innerHTML = '';
      const noResultsMessageDiv = document.getElementById('no-results-message');
      if (noResultsMessageDiv) noResultsMessageDiv.innerHTML = '';
      modalSearchInput.value = '';

      await processSearch(e, mobileSelectedValue, appName);
    });
  }
}

function initScrollButtons() {
  document.addEventListener('click', function(event) {
    if (!event.target.classList.contains('scroll-button')) return;

    const isLeft = event.target.classList.contains('scroll-button-left');
    const sibling = isLeft ? event.target.nextElementSibling : event.target.previousElementSibling;
    const variantList = sibling?.querySelector('.variant-list');
    if (!variantList) return;

    const cards = variantList.querySelectorAll('.variant-card');
    const firstCard = cards[0];
    const otherCard = cards[1] || firstCard;
    if (!firstCard) return;

    const firstCardRect = firstCard.getBoundingClientRect();
    const listRect = variantList.getBoundingClientRect();

    // check if the first card is fully or partially visible
    let scrollAmount;
    if (firstCardRect.left >= listRect.left && firstCardRect.right <= listRect.right) {
      scrollAmount = firstCard.offsetWidth + 40;
    } else {
      scrollAmount = otherCard.offsetWidth + 40;
    }

    if (isLeft) {
      scrollAmount = scrollAmount * -1;
    }

    variantList.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  });
}

function safeInit(name, initFn) {
  try {
    initFn();
  } catch (error) {
    console.error(`[app-compat] ${name} failed to initialize — feature disabled`, error);
  }
}

function initAll() {
  safeInit('desktop search', initDesktopSearch);
  safeInit('suggestions', initSuggestions);
  safeInit('mobile modal', initMobileModal);
  safeInit('scroll buttons', initScrollButtons);

  // Warm the fuzzy-search index in the background.
  setTimeout(() => {
    initializeFuzzySearch().catch(error =>
      console.error('[app-compat] Fuzzy search init failed', error)
    );
  }, 100);
}

if (document.readyState !== 'loading') {
  initAll();
} else {
  document.addEventListener('DOMContentLoaded', initAll);
}
